/**
 * Send an Order and wait PO (BLU-1506): building -> sent. This version sends
 * nothing itself, so the dialog is an instruction, "Order {po} on
 * {supplier}'s website": the buyer checks each blank on the supplier's
 * website, turns on "Out at {supplier}" for any the supplier does not have
 * (those orders keep waiting and the blank moves to the next purchase order),
 * buys the rest there, then presses Mark as sent.
 *
 * Blanks that were out last time start turned on (the mock-up's sticky "out"),
 * so a blank that is still out is not sent by accident; the buyer turns it off
 * once the supplier has it again. A footer line says what Send will do, live.
 *
 * BLU-1526: when the server reads the supplier's own stock for a blank (a
 * blank linked to the PO's catalog supplier), the line shows "{supplier}: N
 * left" with "back {date}" while that day has not passed, and the switch
 * starts on when N is below the quantity (`startsOut`). That number beats the
 * sticky mark both ways; with no number the sticky rule stands. Nothing here
 * changes the store.
 *
 * BLU-1501: when `placement.canPlace` says the claim would pass, the dialog also
 * offers Place order, which sends this order to the supplier through their API,
 * and "I ordered it on the website" is the path above. Otherwise nothing changes.
 * Place sends the exact lines on screen. If the server's are different by then, it
 * changes nothing and says which moved, and the person confirms again. Nothing
 * retries it: the mutation has retry off, and only a press sends it again.
 *
 * Once Place has been pressed, or Place was on offer and is not any more, or the PO
 * is no longer a draft, the website path is gone: the order may be on its way, and a
 * person who buys by hand now buys twice. A Place with no plain answer, or a PO sent
 * while this was open, leaves only "check the PO page", and that holds if the dialog
 * is opened again.
 *
 * Opened from the To buy card and from the PO page's action bar, never from
 * inside another dialog (DESIGN.md section 10). The body mounts on open, so
 * the toggles start fresh every time.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2, PackageCheck, Send } from 'lucide-react';
import type { OrderAndWaitPo } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { NoteLine } from '@/components/shared/NoteLine';
import { WarningNotice } from '@/components/shared/WarningNotice';
import { cn } from '@/lib/utils';
import { useOrderAndWaitPlacement, usePlaceOrderAndWait, useSendOrderAndWait, type PlaceOutcome } from '../hooks/useOrderAndWait';
import {
  blankLines,
  changedPlaceLines,
  expectedPlaceLines,
  initialOutSkuIds,
  previewSend,
  type PlaceLine,
} from '../lib/order-and-wait';
import { jitRowName } from '../lib/jit-lines';
import { SupplierStockNote } from './SupplierStockNote';

interface SendOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The building PO; null keeps the dialog closed. */
  po: OrderAndWaitPo | null;
}

export function SendOrderDialog({ open, onOpenChange, po }: SendOrderDialogProps) {
  const { send, pendingPoId } = useSendOrderAndWait();
  const { place, pendingPoId: placingPoId } = usePlaceOrderAndWait();
  const pending = po !== null && (pendingPoId === po.poId || placingPoId === po.poId);
  /** Outlives the body, which mounts on open: reopening must not bring back the website path. */
  const [unsurePoIds, setUnsurePoIds] = useState<ReadonlySet<string>>(() => new Set());
  return (
    <Dialog open={open && po !== null} onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        {po && (
          <SendOrderBody
            key={po.poId}
            po={po}
            pending={pending}
            unsure={unsurePoIds.has(po.poId)}
            onCancel={() => onOpenChange(false)}
            onSend={async (outSkuIds) => {
              const done = await send(
                { poId: po.poId, poNumber: po.poNumber, supplierName: po.supplierName },
                outSkuIds,
              );
              if (done) onOpenChange(false);
            }}
            onPlace={async (outSkuIds, expected) => {
              const outcome = await place(
                { poId: po.poId, poNumber: po.poNumber, supplierName: po.supplierName },
                outSkuIds,
                expected,
              );
              if (outcome.kind === 'done') onOpenChange(false);
              if (outcome.kind === 'unsure') setUnsurePoIds((current) => new Set(current).add(po.poId));
              return outcome;
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SendOrderBody({
  po,
  pending,
  unsure,
  onCancel,
  onSend,
  onPlace,
}: {
  po: OrderAndWaitPo;
  pending: boolean;
  unsure: boolean;
  onCancel: () => void;
  onSend: (outSkuIds: string[]) => Promise<void>;
  onPlace: (outSkuIds: string[], expected: PlaceLine[]) => Promise<PlaceOutcome>;
}) {
  const { t } = useTranslation('orders');
  const supplier = po.supplierName?.trim() || t('oaw.yourSupplier');
  const lines = useMemo(() => blankLines(po.lines), [po.lines]);
  const [out, setOut] = useState<ReadonlySet<string>>(() => new Set(initialOutSkuIds(lines)));
  const preview = previewSend(lines, out);
  const nothingToSend = preview.sendUnits === 0;
  const previewId = `oaw-send-preview-${po.poId}`;
  const placement = useOrderAndWaitPlacement(po.poId);
  const { canPlace } = placement;
  const [placeTried, setPlaceTried] = useState(false);
  const [seenPlaceable, setSeenPlaceable] = useState(false);
  if (canPlace && !seenPlaceable) setSeenPlaceable(true);
  /** What the server said it would send, after a Place that found the lines had moved. */
  const [changed, setChanged] = useState<{ seen: PlaceLine[]; lines: PlaceLine[] | null } | null>(null);
  const expected = expectedPlaceLines(lines, out);

  const pressPlace = async () => {
    // A second press confirms exactly what the server listed. No list came back: what is on screen now.
    const confirming = changed?.lines ?? expected;
    setPlaceTried(true);
    const outcome = await onPlace([...out], confirming);
    setChanged(outcome.kind === 'lines_changed' ? { seen: confirming, lines: outcome.lines } : null);
  };

  const nameOf = (skuId: string): string => {
    const line = lines.find((candidate) => candidate.skuId === skuId);
    return line ? jitRowName(line) : t('oaw.place.changedOther');
  };

  const toggle = (skuId: string, isOut: boolean) => {
    // A change here means the listed lines are no longer what the person is confirming.
    setChanged(null);
    setOut((current) => {
      const next = new Set(current);
      if (isOut) next.add(skuId);
      else next.delete(skuId);
      return next;
    });
  };

  if (unsure || placement.moved || (seenPlaceable && !canPlace)) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>{t('oaw.place.checkTitle', { po: po.poNumber })}</DialogTitle>
          <DialogDescription>{t('oaw.place.checkBody', { po: po.poNumber, supplier })}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:flex-wrap sm:gap-y-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('oaw.dialog.back')}
          </Button>
          <Button asChild className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90">
            <Link to={`/orders/${po.poId}`} onClick={onCancel}>
              {t('oaw.place.checkOpen', { po: po.poNumber })}
            </Link>
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {canPlace ? t('oaw.place.title', { po: po.poNumber, supplier }) : t('oaw.send.title', { po: po.poNumber, supplier })}
        </DialogTitle>
        <DialogDescription>
          {canPlace ? t('oaw.place.body', { supplier }) : t('oaw.send.body', { supplier })}
        </DialogDescription>
      </DialogHeader>

      {canPlace && (
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">{t('oaw.place.shipTo')}</dt>
          <dd className="min-w-0 break-words">{placement.shipTo}</dd>
          <dt className="text-muted-foreground">{t('oaw.place.method')}</dt>
          <dd className="min-w-0 break-words">{placement.method}</dd>
        </dl>
      )}

      {changed && (
        <WarningNotice role="alert">
          <p className="font-medium">{t('oaw.place.changedTitle')}</p>
          {changed.lines ? (
            <ul className="mt-1 space-y-0.5">
              {changedPlaceLines(changed.seen, changed.lines).map((line) => (
                <li key={line.skuId} className="break-words">
                  {t('oaw.place.changedLine', { name: nameOf(line.skuId), before: line.before, after: line.after })}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1">{t('oaw.place.changedLong')}</p>
          )}
          <p className="mt-1">{t('oaw.place.changedConfirm')}</p>
        </WarningNotice>
      )}

      {lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('oaw.card.empty')}</p>
      ) : (
        <ul className="divide-y rounded-lg border" aria-label={t('oaw.send.linesLabel')}>
          {lines.map((line) => {
            const name = jitRowName(line);
            const isOut = out.has(line.skuId);
            const switchId = `oaw-out-${po.poId}-${line.skuId}`;
            return (
              <li key={line.skuId} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <div className={cn('min-w-0 flex-1 basis-40', isOut && 'text-muted-foreground')}>
                  <p className="break-words text-sm font-medium">{name}</p>
                  <p className="flex flex-wrap gap-x-2 text-[11px] text-muted-foreground">
                    {line.sku && <span className="min-w-0 break-all font-mono">{line.sku}</span>}
                    {line.outUnits > 0 && <span>{t('oaw.send.outLastTime')}</span>}
                    <SupplierStockNote
                      stock={line.supplierStock}
                      eta={line.nextDeliveryEta}
                      supplierName={po.supplierName}
                    />
                  </p>
                </div>
                <span className="w-12 shrink-0 text-right text-sm tabular-nums">
                  <span className="sr-only">{t('oaw.send.qtyAria', { count: line.qty })}</span>
                  <span aria-hidden="true">{line.qty}</span>
                </span>
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <Switch
                    id={switchId}
                    checked={isOut}
                    onCheckedChange={(checked) => toggle(line.skuId, checked)}
                    disabled={pending}
                    aria-label={t('oaw.send.outAria', { supplier, name })}
                  />
                  <label htmlFor={switchId} className="cursor-pointer text-xs text-muted-foreground" aria-hidden="true">
                    {t('oaw.status.out', { supplier })}
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <DialogFooter className="sm:flex-wrap sm:items-center sm:gap-y-2">
        <div id={previewId} role="status" className="text-sm text-muted-foreground sm:mr-auto">
          {nothingToSend ? (
            <NoteLine tone="warning">{t('oaw.send.nothing')}</NoteLine>
          ) : (
            <>
              {t('oaw.send.previewSend', { count: preview.sendUnits })}
              {preview.outUnits > 0 && <> {t('oaw.send.previewOut', { count: preview.outUnits })}</>}
            </>
          )}
        </div>
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          {t('oaw.dialog.back')}
        </Button>
        {canPlace ? (
          <>
            {!placeTried && (
              <Button
                type="button"
                variant="outline"
                onClick={() => void onSend([...out])}
                disabled={pending || nothingToSend}
              >
                {t('oaw.place.website')}
              </Button>
            )}
            <Button
              type="button"
              onClick={() => void pressPlace()}
              disabled={pending || nothingToSend}
              aria-busy={pending || undefined}
              aria-describedby={previewId}
              className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            >
              {pending ? (
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              ) : (
                <PackageCheck className="h-4 w-4" aria-hidden="true" />
              )}
              {changed ? t('oaw.place.confirmAgain') : t('oaw.place.confirm')}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            onClick={() => void onSend([...out])}
            disabled={pending || nothingToSend || placement.loading}
            aria-busy={pending || undefined}
            aria-describedby={previewId}
            className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <Send className="h-4 w-4" aria-hidden="true" />
            )}
            {t('oaw.send.confirm')}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}
